import { css } from '@emotion/react';
import { IconButton } from '../../navigation/IconButton';
import { Typography, Colors } from '../../../theme';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import { clamp } from '../../../utils/clamp';
import { NumberButton } from '../NumberButton';
const Styles = labelStyles('Paginator', {
    root: (derivedState, props) => [
        {
            display: 'grid',
            gap: 10,
            gridTemplateColumns: `repeat(${derivedState.numberOfButtons}, 40px)`,
            alignItems: 'center',
        },
        derivedState.isCondensed &&
            withModifier('condensed', {
                gridTemplateColumns: '44px 44px min-content 44px 44px',
            }),
        props.isCentered &&
            withModifier('isCentered', {
                justifyContent: 'center',
            }),
    ],
    text: [
        css(Object.assign(Object.assign({}, Typography.base), { fontSize: 16, lineHeight: '22px', fontWeight: 600, textAlign: 'center', whiteSpace: 'nowrap', color: Colors.neutral800 })),
        css `
      padding-left: 20px;
      padding-right: 20px;
    `,
    ],
});
const PaginatorText = (props) => {
    const maxChars = `${props.numberOfPages}`.length * 2 + 3;
    return (isolateComponent("span", { css: Styles.text, style: {
            minWidth: `${maxChars}ch`,
        } }, `${props.currentPageIndex + 1} of ${props.numberOfPages}`));
};
const defaultProps = {
    condenseAt: 6,
    offset: 0,
};
export const Paginator = (_props) => {
    const props = Object.assign(Object.assign({}, defaultProps), _props);
    const { currentPageIndex, pageUrls, condenseAt, offset, firstPageUrl, lastPageUrl, } = props;
    const numberOfPages = pageUrls.length;
    const maxPageIndex = numberOfPages - 1;
    const safeCurrentPageIndex = clamp(currentPageIndex, 0, maxPageIndex);
    const prevIndex = Math.max(0, safeCurrentPageIndex - 1);
    const nextIndex = Math.min(maxPageIndex, safeCurrentPageIndex + 1);
    const isCondensed = numberOfPages >= condenseAt;
    const derivedState = {
        numberOfPages,
        isCondensed,
        numberOfButtons: numberOfPages + 2 + [firstPageUrl, lastPageUrl].filter(Boolean).length,
    };
    const createOnPaginateFn = (buttonType, pageIndex, pageUrl) => {
        return (event) => {
            if (props.onPaginate) {
                props.onPaginate({
                    buttonType,
                    pageIndex,
                    pageUrl,
                }, event);
            }
        };
    };
    const content = isCondensed ? (isolateComponent(PaginatorText, { currentPageIndex: safeCurrentPageIndex, numberOfPages: numberOfPages })) : (pageUrls.map((pageUrl, pageIndex) => {
        const isSelected = pageIndex === safeCurrentPageIndex;
        const pageNumber = offset + pageIndex + 1;
        return (isolateComponent(NumberButton, { ariaLabel: isSelected ? `Current page (${pageNumber})` : `${pageNumber}`, href: pageUrl, key: pageUrl || pageNumber, onClick: createOnPaginateFn('number', pageIndex, pageUrl), value: pageNumber, selected: isSelected, hasShadow: isSelected }));
    }));
    const firstPageButtonUrl = firstPageUrl || (isCondensed ? pageUrls[0] : undefined);
    const firstPageButton = (firstPageButtonUrl || isCondensed) && (isolateComponent(IconButton, { ariaLabel: "First page", color: "icon-secondary", disabled: currentPageIndex <= 0, href: firstPageButtonUrl, icon: "arrowLineLeft", onClick: createOnPaginateFn('first', 0, firstPageButtonUrl), tooltipPosition: "southwest" }));
    const lastPageButtonUrl = lastPageUrl || (isCondensed ? pageUrls[maxPageIndex] : undefined);
    const lastPageButton = (lastPageButtonUrl || isCondensed) && (isolateComponent(IconButton, { ariaLabel: "Last page", color: "icon-secondary", disabled: safeCurrentPageIndex >= maxPageIndex, href: lastPageButtonUrl, icon: "arrowLineRight", onClick: createOnPaginateFn('last', maxPageIndex, lastPageButtonUrl), tooltipPosition: "southeast" }));
    return (isolateComponent("nav", { "aria-label": props.ariaLabel, css: [Styles.root(derivedState, props)] },
        firstPageButton,
        isolateComponent(IconButton, { ariaLabel: `Previous page (${prevIndex + 1})`, color: "icon-secondary", disabled: safeCurrentPageIndex <= 0, href: pageUrls[prevIndex], icon: "arrowSmallLeft", onClick: createOnPaginateFn('previous', prevIndex, pageUrls[prevIndex]), tooltipPosition: "southwest" }),
        content,
        isolateComponent(IconButton, { ariaLabel: `Next page (${nextIndex + 1})`, color: "icon-secondary", disabled: safeCurrentPageIndex >= maxPageIndex, href: pageUrls[nextIndex], icon: "arrowSmallRight", onClick: createOnPaginateFn('next', nextIndex, pageUrls[nextIndex]), tooltipPosition: "southeast" }),
        lastPageButton));
};
//# sourceMappingURL=Paginator.js.map
// @zapier/design-system@5.32.5 (2004a9d50)
